import { useEffect, useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Trans } from "@lingui/react/macro";

import { env } from "../../env";

type AuthMode = "magic-link" | "password";
type PasswordStep = "signIn" | "signUp";

interface SignInCardProps {
  initialMode?: AuthMode;
  initialStep?: PasswordStep;
  onEmailSentChange?: (emailSent: boolean) => void;
}

export default function SignInCard({
  initialMode = "magic-link",
  initialStep = "signUp",
  onEmailSentChange,
}: SignInCardProps) {
  const { signIn } = useAuthActions();
  const isDevMode = env.VITE_DEV_LOGIN === "true";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<AuthMode>(
    isDevMode ? initialMode : "magic-link",
  );
  const [step, setStep] = useState<PasswordStep>(initialStep);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    onEmailSentChange?.(emailSent);
  }, [emailSent, onEmailSentChange]);

  const handleGoogle = () => {
    signIn("google", {
      redirectTo: `${window.location.pathname}${window.location.search}`,
    });
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      return;
    }
    setLoading(true);
    try {
      await signIn("resend", { email });
      setEmailSent(true);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.set("email", email);
      formData.set("password", password);
      formData.set("flow", step);
      await signIn("password", formData);
    } catch {
      setError(
        step === "signIn"
          ? "Correo o contraseña incorrectos."
          : "No se pudo crear la cuenta. Intenta con otro correo.",
      );
    }
    setLoading(false);
  };

  const handleDevLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.set("email", "test@bodaweb.site");
      formData.set("password", "testpassword123");
      formData.set("flow", "signIn");
      await signIn("password", formData);
    } catch {
      setError("Dev login failed.");
    }
    setLoading(false);
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError("");
    setEmailSent(false);
  };

  return (
    <div className="app-card mx-auto w-full max-w-sm p-8">
      <h2 className="mb-6 text-center font-display text-4xl italic leading-none text-[var(--color-ink)]">
        <Trans>Iniciar Sesión</Trans>
      </h2>

      {isDevMode && (
        <>
          <button
            className="flex w-full cursor-pointer items-center justify-center gap-2 bg-[var(--color-accent)] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-black disabled:opacity-50"
            data-testid="dev-login-button"
            disabled={loading}
            onClick={handleDevLogin}
            type="button"
          >
            {loading ? "..." : "Dev Login (test@bodaweb.site)"}
          </button>
          <div className="my-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-[var(--color-border)]" />
            <span className="text-xs uppercase text-[var(--color-muted)]">
              o
            </span>
            <div className="h-px flex-1 bg-[var(--color-border)]" />
          </div>
        </>
      )}

      {!emailSent && (
        <>
          <button
            className="flex w-full cursor-pointer items-center justify-center gap-2 border border-[var(--color-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--color-ink)] transition-colors hover:bg-[var(--color-page-soft)]"
            data-testid="google-signin-button"
            onClick={handleGoogle}
            type="button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <Trans>Continuar con Google</Trans>
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-[var(--color-border)]" />
            <span className="text-xs uppercase text-[var(--color-muted)]">
              <Trans>o</Trans>
            </span>
            <div className="h-px flex-1 bg-[var(--color-border)]" />
          </div>
        </>
      )}

      {mode === "magic-link" ? (
        <>
          {emailSent ? (
            <div className="text-center space-y-2">
              <p className="font-medium text-[var(--color-accent)]">
                <Trans>¡Enlace enviado!</Trans>
              </p>
              <p className="text-sm text-[var(--color-muted)]">
                <Trans>Revisa tu correo electrónico para iniciar sesión.</Trans>
              </p>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={handleEmail}>
              <input
                className="app-input w-full text-sm"
                data-testid="magic-link-email-input"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                type="email"
                value={email}
              />
              <button
                className="app-btn-primary w-full px-4 py-2.5 text-sm disabled:opacity-50"
                data-testid="magic-link-submit-button"
                disabled={loading}
                type="submit"
              >
                {loading ? (
                  <Trans>Enviando...</Trans>
                ) : (
                  <Trans>Enviar enlace de acceso</Trans>
                )}
              </button>
            </form>
          )}
          {isDevMode && (
            <button
              className="mt-3 w-full cursor-pointer text-xs text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
              data-testid="toggle-password-mode"
              onClick={() => switchMode("password")}
              type="button"
            >
              <Trans>Usar correo y contraseña</Trans>
            </button>
          )}
        </>
      ) : (
        <>
          <form className="space-y-3" onSubmit={handlePassword}>
            <input
              className="app-input w-full text-sm"
              data-testid="password-email-input"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              required
              type="email"
              value={email}
            />
            <div className="relative">
              <input
                className="app-input w-full pr-9 text-sm"
                data-testid="password-password-input"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                required
                type={showPassword ? "text" : "password"}
                value={password}
              />
              <button
                className="absolute inset-y-0 right-0 flex cursor-pointer items-center px-2.5 text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                type="button"
              >
                {showPassword ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 1l22 22"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            {error && (
              <p className="text-xs text-[var(--color-danger)]">{error}</p>
            )}
            <button
              className="app-btn-primary w-full rounded-2xl px-4 py-2.5 text-sm disabled:opacity-50"
              data-testid="password-submit-button"
              disabled={loading}
              type="submit"
            >
              {loading ? (
                "..."
              ) : step === "signIn" ? (
                <Trans>Iniciar Sesión</Trans>
              ) : (
                <Trans>Crear Cuenta</Trans>
              )}
            </button>
          </form>
          <button
            className="mt-2 w-full cursor-pointer text-xs text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
            data-testid="password-step-toggle"
            onClick={() => {
              setStep(step === "signIn" ? "signUp" : "signIn");
              setError("");
            }}
            type="button"
          >
            {step === "signIn" ? (
              <Trans>¿No tienes cuenta? Crear una</Trans>
            ) : (
              <Trans>¿Ya tienes cuenta? Iniciar sesión</Trans>
            )}
          </button>
          <button
            className="mt-1 w-full cursor-pointer text-xs text-[var(--color-muted)] transition-colors hover:text-[var(--color-ink)]"
            onClick={() => switchMode("magic-link")}
            type="button"
          >
            <Trans>Usar enlace de acceso</Trans>
          </button>
        </>
      )}
    </div>
  );
}
